import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Check, X } from 'lucide-react'; // Ícone X adicionado aqui
import { useLanguage } from '@/context/LanguageContext';
import { trackEvent } from '@/lib/analytics';
import { suitesData } from '@/data/suitesData';

const SuiteDetail = () => {
  const { id } = useParams();
  const { t } = useLanguage();
  const suite = suitesData.find((s) => s.id === id);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);

  if (!suite) {
    return (
      <div className="min-h-screen bg-[#f6f6f8] pt-28 pb-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold mb-4">Suíte não encontrada</h1>
          <Link to="/suites" className="text-[#C5A059] font-semibold hover:underline">
            {t('suites.backToSuites')}
          </Link>
        </div>
      </div>
    );
  }

  const getSuiteTranslation = () => {
    // As comodidades ficam nos arquivos de idioma (src/translations/*.js).
    // Se faltar a lista em algum idioma, t() devolve a própria chave (texto), então usamos lista vazia.
    const amenities = t(`suites.${suite.id}.amenities`);
    return {
      name: t(`suites.${suite.id}.name`),
      subtitle: t(`suites.${suite.id}.subtitle`),
      description: t(`suites.${suite.id}.description`),
      amenities: Array.isArray(amenities) ? amenities : []
    };
  };

  const translation = getSuiteTranslation();

  const sendWhatsApp = () => {
    const phone = '5531991640648';
    const message = t('whatsapp.reserveSuite').replace('{suite}', translation.name);
    trackEvent('whatsapp_click', { origem: 'suite_reservar', suite: suite.id, page_path: window.location.pathname });
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-[#f6f6f8] pt-28 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link
          to="/suites"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-[#C5A059] font-semibold mb-8 transition-colors"
          data-testid="back-to-suites-button"
        >
          <ArrowLeft className="w-5 h-5" />
          {t('suites.backToSuites')}
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Gallery */}
          <div className="space-y-4 animate-slide-in-left" data-testid="suite-gallery">
            {/* Main Image */}
            <div
              className="relative h-[500px] rounded-2xl overflow-hidden cursor-pointer image-hover-zoom"
              onClick={() => setShowImageModal(true)}
            >
              <img
                src={suite.gallery[selectedImage]}
                alt={`${translation.name} - ${selectedImage + 1}`}
                className="w-full h-full object-cover"
                data-testid="suite-main-image"
              />
            </div>

            {/* Thumbnail Gallery */}
            <div className="grid grid-cols-4 gap-3">
              {suite.gallery.map((image, index) => (
                <div
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`h-24 rounded-lg overflow-hidden cursor-pointer transition-all ${
                    selectedImage === index
                      ? 'ring-4 ring-[#C5A059] scale-105'
                      : 'hover:ring-2 ring-gray-300'
                  }`}
                  data-testid={`suite-thumbnail-${index}`}
                >
                  <img src={image} alt={`Thumbnail ${index + 1}`} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>

          {/* Details */}
          <div className="space-y-8 animate-slide-in-right" data-testid="suite-details">
            <div>
              <h1 className="text-4xl font-extrabold text-slate-900 mb-2">{translation.name}</h1>
              <p className="text-xl text-[#C5A059] font-semibold">{translation.subtitle}</p>
            </div>

            {/* Description */}
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">{t('suites.aboutSuite')}</h2>
              <p className="text-gray-600 leading-relaxed">{translation.description}</p>
            </div>

            {/* Amenities */}
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">{t('suites.amenities')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {translation.amenities.map((amenity, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#C5A059] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">{amenity}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <button
              onClick={sendWhatsApp}
              className="w-full btn-gold rounded-xl px-8 py-4 font-bold text-lg shadow-xl"
              data-testid="suite-reserve-button"
            >
              {t('suites.reserveThisSuite')}
            </button>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {showImageModal && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setShowImageModal(false)}
          data-testid="image-modal"
        >
          <button
            className="absolute top-4 right-4 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
            onClick={() => setShowImageModal(false)}
            data-testid="close-modal-button"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <img
            src={suite.gallery[selectedImage]}
            alt={translation.name}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default SuiteDetail;
